import {
    DropdownV2,
    PriorityIcon,
    Icon,
    TriggerWrapperV2,
    Button,
    ListItem,
    Text,
    Flex,
    DropdownSection,
    Scrollbar
} from '@priority-software/priority-style-react';
import React, {useCallback, useMemo, useRef} from 'react';
import {MacroDropdownItem, SubMenuItem} from './Item';
import {useKeyboardNavigation} from '../../../../../components/Common/FieldSearch/hooks/useKeyboardNavigation';
import {formsStore} from '../../../../../stores/Form';
import styles from './styles.module.scss';
import {isRTL} from '../../../../../Localization';
import {getDropdownDirections} from '../../../../Form/utils/dropdownDirections';
import {createDataTestId} from '../../../../../utils/dataTestId';
import {texts} from '../../../texts';
import {AutomationSettingsItem, macroItem} from '../../AutomationsDropdown/constants';

//Exmpl: For rendering view submenus item
const subMenuItems = [...formsStore.macros, {title: 'Test 8888888888888888888888', id: '1'}];

type Props = {
    isOpen: boolean;
    openMacro: (item: AutomationSettingsItem) => void;
    closeSubMenu: () => void;
};

export const MacroMenuDropdown = ({isOpen, openMacro, closeSubMenu}: Props) => {
    const triggerRef = useRef<HTMLButtonElement>(null);

    const configureTrigger = triggerRef.current?.getBoundingClientRect();

    const style = useCallback(() => {
        if (isRTL()) {
            return getDropdownDirections(configureTrigger, isRTL(), 200) === 'leftSide'
                ? {top: -20, left: 240, right: 'auto'}
                : {top: -20, right: 60, left: 'auto'};
        }
        return getDropdownDirections(configureTrigger, isRTL(), 200) === 'leftSide'
            ? {top: -20, left: 'auto', right: 240}
            : {top: -20, right: -235, left: 'auto'};
    }, [configureTrigger, isRTL]);

    const handleSelectFocusedIndex = async () => {
        onItemClick(subMenuItems[focusedIndex] as SubMenuItem);
    };

    const {focusedIndex, handleKeyboardNavigation} = useKeyboardNavigation({
        initialFocusedIndex: 0,
        listLength: subMenuItems.length,
        pageSize: 5,
        onSelect: handleSelectFocusedIndex,
        onNavigateLeft: closeSubMenu
    });

    const handleEdit = (e: Event, item: SubMenuItem) => {
        e.stopPropagation();
    };

    const handleDelete = (e: Event, item: SubMenuItem) => {
        e.stopPropagation();
    };

    const onItemClick = (item: SubMenuItem) => {
        console.log('item');
    };

    const handleOpenMacroModal = e => {
        e.stopPropagation();
        openMacro(macroItem);
    };

    const handleDeleteItems = e => {
        e.stopPropagation();
    };

    const renderScrollList = useMemo(
        () => (
            <Scrollbar
                autoHeight
                autoHide={false}
                rtl={isRTL()}
                componentClasses={styles.scroll}
                autoHeightMax={205}
            >
                {subMenuItems.map((item, index) => (
                    <MacroDropdownItem
                        item={item as SubMenuItem}
                        key={item.title}
                        onItemClick={onItemClick}
                        index={index}
                        currentFocusedIndex={focusedIndex}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                    />
                ))}
            </Scrollbar>
        ),
        [subMenuItems, isRTL]
    );

    const arrow = useMemo(() => {
        if (isRTL()) {
            return isOpen ? PriorityIcon.arrowRight : PriorityIcon.arrowLeft;
        }
        return isOpen ? PriorityIcon.arrowLeft : PriorityIcon.arrowRight;
    }, [isRTL, isOpen]);

    return (
        <DropdownV2
            oldStyles
            isOpen={isOpen}
            onKeyDown={handleKeyboardNavigation}
            contentComponentClasses={styles.dropdownContainer}
            contentStyle={isOpen && style()}
            trigger={
                <TriggerWrapperV2 isOpen={isOpen} className={styles.triggerWrapper}>
                    <Button
                        skin="text"
                        inputRef={triggerRef}
                        componentClasses={styles.buttonTrigger}
                    >
                        <Icon icon={arrow} />
                    </Button>
                </TriggerWrapperV2>
            }
            {...createDataTestId('macro-subForm-dropdown', true)}
        >
            <DropdownSection>
                <ListItem onClick={handleDeleteItems} isFocused={false} isActive={false}>
                    <Flex justifyContent="space-between">
                        <Text tag="div" skin="label-1">
                            {texts.macro_submenu_delete_lines}
                        </Text>

                        <Text tag="div" skin="caption-2" textColor="secondary-400">
                            [ALT + 10]
                        </Text>
                    </Flex>
                </ListItem>
                {renderScrollList}
                <ListItem onClick={handleOpenMacroModal} isFocused={false} isActive={false}>
                    <Flex justifyContent="flex-start" componentClasses={styles.recordMacro}>
                        <Icon icon={PriorityIcon.play} />
                        <Text tag="div" skin="label-1" textColor="secondary-500">
                            {texts.marco_submenu_record_button}
                        </Text>
                    </Flex>
                </ListItem>
            </DropdownSection>
        </DropdownV2>
    );
};
